import { useState, useEffect, useCallback, useMemo, useRef, useId } from "react";
import { ShoppingList, ShoppingItem, ShoppingItemWithProduct } from "@/types";
import CategoryListView from "./CategoryListView";
import VirtualizedCategoryListView from './VirtualizedCategoryListView';
import { useItemMutations } from "./item-list/useItemMutations";
import EditItemDialog from "./edit-item/EditItemDialog";
import { useShoppingItemsAndCategories } from "@/hooks/shopping/useShoppingItemsAndCategories";
import ErrorBoundary from "../common/ErrorBoundary";
import { ErrorAlert } from "../ui/error-alert";
import { useModalStore } from "@/services/modalStore";
import { useOfflineShoppingPageStatus } from "@/hooks/useOfflineShoppingPageStatus";
import { OfflineShoppingListStatus } from "./OfflineShoppingListStatus";
import { networkService } from "@/services/network.service";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import { getThemeVariable } from "@/utils/theme";
import { getProductForShoppingItem } from "@/services/shopping/items/index";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

const toWithProduct = (item: ShoppingItem): ShoppingItemWithProduct => {
  const itemWithName = item as ShoppingItem & { name?: string };
  return { ...itemWithName, name: itemWithName.name ?? "" };
};

interface ShoppingContentProps {
  list: ShoppingList;
  filter: string;
  selectedCategories: string[];
  searchTerm: string;
  sortTrigger?: number;
  backgroundColor?: string;
  searchOpen?: boolean;
  activeShopId?: string;
  onShareableItemsChange?: (items: ShoppingItemWithProduct[]) => void;
}

export default function ShoppingContent({
  list,
  filter,
  selectedCategories,
  searchTerm,
  sortTrigger = 0,
  searchOpen = false,
  activeShopId,
  backgroundColor,
  onShareableItemsChange,
}: ShoppingContentProps) {
  const modalState = useModalStore();
  const [itemToEdit, setItemToEdit] = useState<ShoppingItem | null>(null);
  const [viewMode, setViewMode] = useState<"grid" | "list">(() => {
    const stored = localStorage.getItem("shopping-view-mode");
    return stored === "list" ? "list" : "grid";
  });
  const listaDescId = useId();

  useEffect(() => {
    localStorage.setItem("shopping-view-mode", viewMode);
  }, [viewMode]);

  const {
    isOnline,
    isLoading: statusLoading,
    hasErrors,
    errors,
    data: { shoppingItems, categories: userCategories },
  } = useOfflineShoppingPageStatus({ listId: list.id });

  const {
    isManualSorting,
    handleDragEndWrapper,
    setOptimisticItems,
    optimisticItems,
    isLoading: itemsLoading,
    error: itemsError,
  } = useShoppingItemsAndCategories(list.id);

  const { handleCheckItem, handleDeleteItem } = useItemMutations({
    listId: list.id,
    onOptimisticUpdate: (items) => {
      setOptimisticItems(items.map(toWithProduct));
    },
  });

  const [productNameCache, setProductNameCache] = useState<
    Record<string, string>
  >({});

  const optimisticItemsSafe = optimisticItems ?? [];
  const userCategoriesSafe = userCategories ?? [];
  const shoppingItemsSafe = shoppingItems ?? [];

  useEffect(() => {
    if (!shoppingItemsSafe?.length) return;
    const ids = new Set(shoppingItemsSafe.map((i) => i.id));
    setOptimisticItems((prev) => prev.filter((o) => !ids.has(o.id)));
  }, [shoppingItemsSafe]);

  // Always overlay optimistic items onto server data, preferring newest timestamps
  const mergedItems: ShoppingItemWithProduct[] = useMemo(() => {
    const map = new Map<string, ShoppingItemWithProduct>(
      shoppingItemsSafe
        .map(toWithProduct)
        .map((item) => [item.id, item]),
    );
    optimisticItemsSafe.forEach((o) => {
      const base = map.get(o.id);
      const baseTs = base?.updated_at ? Date.parse(base.updated_at) : 0;
      const optTs = o.optimisticUpdatedAt ?? 0;
      if (optTs > baseTs) {
        map.set(o.id, { ...base, ...o });
      }
    });
    return Array.from(map.values());
  }, [shoppingItemsSafe, optimisticItemsSafe]);

  useEffect(() => {
    if (!searchTerm) return;
    if (!networkService.isOnline()) {
      console.warn(
        "[ShoppingContent] Offline - skipping product name fetch",
      );
      return;
    }

    const missingIds = mergedItems
      .filter((item) => item.product_id && !productNameCache[item.product_id])
      .map((item) => item.product_id);

    if (missingIds.length === 0) return;

    let cancelled = false;

    const fetchNames = async () => {
      const results = await Promise.all(
        missingIds.map(async (id) => {
          try {
            const product = await getProductForShoppingItem(id);
            return product?.name ? [id, product.name] : null;
          } catch (error) {
            console.error("Failed to fetch product name", error);
            return null;
          }
        }),
      );

      if (!cancelled) {
        setProductNameCache((prev) => {
          const newCache = { ...prev };
          results.forEach((res) => {
            if (res) {
              const [id, name] = res;
              newCache[id] = name;
            }
          });
          return newCache;
        });
      }
    };

    fetchNames();
    return () => {
      cancelled = true;
    };
  }, [mergedItems, searchTerm]);

  // Simplified filtering and sorting
  const filteredItemsRef = useRef<ShoppingItemWithProduct[]>([]);

  const filteredItems: ShoppingItemWithProduct[] = useMemo(() => {
    let filtered: ShoppingItemWithProduct[] = mergedItems;

    // Apply search filter
    if (searchTerm) {
      const searchTermLower = searchTerm.toLowerCase();
      filtered = filtered.filter((item) => {
        const productName =
          item.product?.name ||
          productNameCache[item.product_id] ||
          item.name ||
          "";
        return productName.toLowerCase().includes(searchTermLower);
      });
    }

    // Apply category filter
    if (selectedCategories.length > 0) {
      filtered = filtered.filter((item) =>
        selectedCategories.includes(item.category),
      );
    }

    // Apply status filter
    if (filter === "active") {
      filtered = filtered.filter((item) => !item.checked);
    } else if (filter === "completed") {
      filtered = filtered.filter((item) => item.checked);
    }

    // Sort items
    const sorted = [...filtered].sort((a, b) => {
      // First by checked status
      if (a.checked !== b.checked) return a.checked ? 1 : -1;

      // Then by category position
      const catA = userCategoriesSafe.find((cat) => cat.id === a.category);
      const catB = userCategoriesSafe.find((cat) => cat.id === b.category);
      const posA = typeof catA?.position === "number" ? catA.position : 999;
      const posB = typeof catB?.position === "number" ? catB.position : 999;
      if (posA !== posB) return posA - posB;

      // Finally by name
      const nameA =
        a.product?.name ||
        productNameCache[a.product_id] ||
        a.name ||
        "";
      const nameB =
        b.product?.name ||
        productNameCache[b.product_id] ||
        b.name ||
        "";
      return nameA.localeCompare(nameB);
    });

    const previous = filteredItemsRef.current;
    const hasDifferences =
      previous.length !== sorted.length ||
      sorted.some((item, index) => {
        const prevItem = previous[index];
        if (!prevItem) return true;
        if (prevItem.id !== item.id) return true;
        if (prevItem.checked !== item.checked) return true;
        if ((prevItem.updated_at ?? null) !== (item.updated_at ?? null)) return true;
        if (
          (prevItem.optimisticUpdatedAt ?? null) !==
          (item.optimisticUpdatedAt ?? null)
        )
          return true;
        if ((prevItem.quantity ?? null) !== (item.quantity ?? null)) return true;
        return false;
      });

    if (!hasDifferences) {
      return previous;
    }

    filteredItemsRef.current = sorted;
    return sorted;
  }, [
    mergedItems,
    searchTerm,
    selectedCategories,
    filter,
    userCategoriesSafe,
    sortTrigger,
    productNameCache,
  ]);

  const lastShareablePayloadRef = useRef<string | null>(null);

  useEffect(() => {
    if (!onShareableItemsChange) return;

    const payloadSignature = `${filteredItems.length}:${filteredItems
      .map((item) => `${item.id}:${item.checked ? 1 : 0}:${item.updated_at ?? ""}`)
      .join("|")}`;

    if (lastShareablePayloadRef.current === payloadSignature) {
      return;
    }

    lastShareablePayloadRef.current = payloadSignature;
    onShareableItemsChange(filteredItems);
  }, [filteredItems, onShareableItemsChange]);

  // Simplified handlers
  const handleCheckItemWrapper = useCallback(
    (id: string, checked: boolean) => {
      handleCheckItem(id, checked, mergedItems);
    },
    [handleCheckItem, mergedItems],
  );

  const handleDeleteItemWrapper = useCallback(
    (id: string) => {
      // Pass current items so undo can restore the deleted one
      handleDeleteItem(id, mergedItems);
    },
    [handleDeleteItem, mergedItems],
  );

  const handleEditItem = useCallback(
    (item: ShoppingItem) => {
      setItemToEdit(item);
      modalState.openModal("edit-item-dialog");
    },
    [modalState],
  );

  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        modalState.closeModal();
        setItemToEdit(null);
      }
    },
    [modalState],
  );

  const isEditDialogOpen = modalState.activeModal === "edit-item-dialog";

  const { theme } = useThemeConfig();
  const safeAreaTop = parseFloat(getThemeVariable('safe-area-top')) || 0;
  // Account for the extra space used in ShoppingHeader to avoid the status bar
  const paddingTop = safeAreaTop + 25 + (searchOpen ? 265 : list.shop_id ? 120 : 90) + 5;

  const handleRetry = useCallback(() => {
    window.location.reload();
  }, []);

  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const toggleViewMode = useCallback(() => {
    setViewMode((prev) => (prev === "grid" ? "list" : "grid"));
  }, []);

  const [listHeight, setListHeight] = useState(0);
  useEffect(() => {
    const updateHeight = () => {
      setListHeight(window.innerHeight - paddingTop - 120);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [paddingTop]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const diffX = e.changedTouches[0].clientX - touchStartX.current;
    const diffY = e.changedTouches[0].clientY - touchStartY.current;
    if (Math.abs(diffX) > 50 && Math.abs(diffX) > Math.abs(diffY)) {
      toggleViewMode();
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  const isLoading = statusLoading || itemsLoading;
  const error = itemsError;

  if (isLoading) {
    return (
      <main
        className="container max-w-md pb-20 px-4 flex justify-center items-center"
        style={{ background: backgroundColor, paddingTop: `${paddingTop}px` }}
      >
        <div className="text-center space-y-2">
          <div
            className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto"
            style={{ borderColor: theme.primaryColor }}
          ></div>
          <p className="text-center">
            {!isOnline
              ? "Caricamento dati offline..."
              : "Caricamento elementi..."}
          </p>
        </div>
      </main>
    );
  }

  if (error && isOnline) {
    return (
      <main
        className="container max-w-md pb-20 px-4 flex justify-center items-center"
        style={{ background: backgroundColor, paddingTop: `${paddingTop}px` }}
      >
        <ErrorAlert
          title="Errore di caricamento"
          description={
            error.message || "Impossibile caricare la lista della spesa"
          }
          onRetry={handleRetry}
        />
      </main>
    );
  }

  if (!shoppingItems || !userCategories) return null;

  const hasItems = mergedItems.length > 0;
  const hasCategories = userCategoriesSafe.length > 0;

  if (!hasItems) {
    return (
      <main
        className="container max-w-md pb-20 px-4 flex justify-center items-center"
        style={{ background: backgroundColor, paddingTop: `${paddingTop}px` }}
      >
        <div className="w-full space-y-4">
          <OfflineShoppingListStatus />
          <div className="py-10 text-center">
            <p style={{ color: theme.sectionTextColor }}>
              {!isOnline
                ? "Nessun articolo disponibile offline. Aggiungi articoli quando sei online."
                : "Nessun articolo nella lista. Aggiungi articoli usando il pulsante in basso."}
            </p>
          </div>
        </div>
      </main>
    );
  }

  if (!hasCategories) {
    return (
      <main
        className="container max-w-md pb-20 px-4 flex justify-center items-center"
        style={{ background: backgroundColor, paddingTop: `${paddingTop}px` }}
      >
        <div className="w-full space-y-4">
          <OfflineShoppingListStatus />
          <div className="py-10 text-center">
            <p style={{ color: theme.sectionTextColor }}>
              {!isOnline
                ? "Nessuna categoria disponibile offline."
                : "Nessuna categoria disponibile. Aggiungi categorie nelle impostazioni."}
            </p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <>
      <main
        className="container max-w-md pb-20 px-4 transition-all duration-300 ease-in-out relative"
        style={{ background: backgroundColor, paddingTop: `${paddingTop}px` }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            {/* z-20 keeps the toggle badge above the left color strip */}
            <button
              className={`fixed top-1/2 -translate-y-1/2 ${viewMode === "grid" ? "right-3 translate-x-1/2" : "left-3 -translate-x-1/2"} rotate-90 text-xs cursor-pointer select-none hover:bg-[var(--btn-bg-hover)] z-20`}
              style={{
                backgroundColor: theme.primaryColor,
                color: theme.textPrimaryColor,
                borderRadius: theme.buttonBorderRadius,
                padding: theme.buttonPadding,
                '--btn-bg-hover': theme.primaryColor,
              } as React.CSSProperties}
              onClick={toggleViewMode}
              aria-describedby={viewMode === 'grid' ? listaDescId : undefined}
            >
              {viewMode === 'grid' ? 'Modalità Lista' : 'Modalità Grafica'}
            </button>
          </TooltipTrigger>
          {viewMode === 'grid' && (
            <TooltipContent>
              Gestisci e organizza la lista della spesa.
            </TooltipContent>
          )}
        </Tooltip>
        {viewMode === 'grid' && (
          <span id={listaDescId} className="sr-only">
            Gestisci e organizza la lista della spesa.
          </span>
        )}

        {hasErrors && (
          <div
            className="mb-4 p-3 rounded-lg border"
            style={{
              backgroundColor: theme.cardBackgroundColor,
              color: theme.errorColor,
              borderColor: theme.errorColor,
            }}
          >
            <p className="text-sm font-medium">Errori di sincronizzazione:</p>
            <ul className="text-xs mt-1">
              {errors.map((error, index) => (
                <li key={index}>• {error}</li>
              ))}
            </ul>
          </div>
        )}

        <ErrorBoundary>
          {viewMode === 'list' ? (
            <VirtualizedCategoryListView
              listId={list.id}
              items={filteredItems}
              categories={userCategoriesSafe}
              onCheckItem={handleCheckItemWrapper}
              onDeleteItem={handleDeleteItemWrapper}
              onEditItem={handleEditItem}
              rapidInsert={list.rapid_insert === false}
              shopId={activeShopId}
              height={listHeight}
            />
          ) : (
            <CategoryListView
              listId={list.id}
              items={filteredItems}
              categories={userCategoriesSafe}
              onCheckItem={handleCheckItemWrapper}
              onDeleteItem={handleDeleteItemWrapper}
              onEditItem={handleEditItem}
              rapidInsert={list.rapid_insert === false}
              shopId={activeShopId}
              viewMode={viewMode}
            />
          )}

          {itemToEdit && (
            <EditItemDialog
              item={itemToEdit}
              isOpen={isEditDialogOpen}
              onOpenChange={handleDialogOpenChange}
              onItemUpdate={(updated) => {
                if (updated) {
                  setOptimisticItems((prev) =>
                    prev.map((i) =>
                      i.id === updated.id
                        ? { ...i, ...updated, name: (updated as any).name ?? i.name ?? "" }
                        : i
                    ),
                  );
                } else {
                  setOptimisticItems((prev) =>
                    prev.filter((i) => i.id !== itemToEdit.id),
                  );
                }
                modalState.closeModal();
                setItemToEdit(null);
              }}
              listId={list.id}
            />
          )}
        </ErrorBoundary>
      </main>

    </>
  );
}
